/**
 * @fileoverview CategoryAddForm component for adding new budget categories.
 *
 * Provides a form interface for creating new categories with:
 * - Category name input
 * - Start date selection (normalized to first of month)
 * - Optional starting balance (only when category starts with budget)
 * - Transaction type selection (Expense/Transfer toggles)
 * - Validation requiring at least one transaction type
 *
 * @module components/CategoryManagement/CategoryAddForm
 */
import { useMemo } from 'react';
import { formatDateForDisplay, getFirstDayOfMonth } from '../../utils/dateUtils';
import ToggleSwitch from '../shared/ToggleSwitch';
import styles from './CategoryAddForm.module.css';

/**
 * CategoryAddForm Component
 *
 * Form for adding a new category with optional starting balance and transaction type selection.
 *
 * @param {Object} props - Component props
 * @param {string} props.newCategoryName - Current category name input value
 * @param {Function} props.setNewCategoryName - Setter for category name
 * @param {string} props.startDate - Selected start date
 * @param {Function} props.handleStartDateChange - Handler for start date changes
 * @param {string} props.startingBalance - Starting balance input value
 * @param {Function} props.setStartingBalance - Setter for starting balance
 * @param {string} props.budgetStartMonth - Budget start month for validation
 * @param {Function} props.handleAddCategory - Handler for add category submission
 * @param {Function} props.setShowAddForm - Function to hide the form
 * @param {Function} props.setError - Function to set error messages
 * @param {boolean} props.allowExpense - Whether category allows expense transactions
 * @param {Function} props.setAllowExpense - Setter for allowExpense toggle
 * @param {boolean} props.allowTransfer - Whether category allows transfer transactions
 * @param {Function} props.setAllowTransfer - Setter for allowTransfer toggle
 * @param {string} props.validationError - Validation error message for transaction types
 */
function CategoryAddForm({
    newCategoryName,
    setNewCategoryName,
    startDate,
    handleStartDateChange,
    startingBalance,
    setStartingBalance,
    budgetStartMonth,
    handleAddCategory,
    setShowAddForm,
    setError,
    allowExpense,
    setAllowExpense,
    allowTransfer,
    setAllowTransfer,
    validationError
}) {
    // Check if category starts at the same time as the budget
    // Use useMemo to only recalculate when startDate or budgetStartMonth changes
    const categoryStartsWithBudget = useMemo(() => {
        const budgetFirstDay = getFirstDayOfMonth(budgetStartMonth);
        return startDate === budgetFirstDay;
    }, [startDate, budgetStartMonth]);

    return (
        <div className={styles.formContainer}>
            <h4 className={styles.heading}>Add New Category</h4>
            <div className={styles.formField}>
                <label className={styles.label}>
                    Category Name:
                </label>
                <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="e.g., Clothing"
                    className={styles.input}
                />
            </div>
            <div className={styles.formField}>
                <label className={styles.label}>
                    Start Date (will use first of month):
                </label>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    min={budgetStartMonth ? budgetStartMonth.split('T')[0] : undefined}
                    className={styles.input}
                />
                <small className={styles.helperText}>
                    Automatically set to: {formatDateForDisplay(startDate)}
                    {budgetStartMonth && ` (Budget starts: ${formatDateForDisplay(budgetStartMonth)})`}
                </small>
            </div>

            {/* Starting Balance - only show if category starts with budget */}
            {categoryStartsWithBudget && (
                <div className={styles.formField}>
                    <label className={styles.label}>
                        Starting Balance (optional):
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        value={startingBalance}
                        onChange={(e) => setStartingBalance(e.target.value)}
                        placeholder="0.00"
                        className={styles.input}
                    />
                    <small className={styles.helperText}>
                        Enter the amount already in this category when starting your budget
                    </small>
                </div>
            )}

            {/* Transaction Type Toggles */}
            <div className={styles.formField}>
                <label className={styles.label}>
                    Transaction Types:
                </label>
                <div className={styles.toggleGroup}>
                    <ToggleSwitch
                        checked={allowExpense}
                        onChange={setAllowExpense}
                        label="Expense"
                    />
                    <ToggleSwitch
                        checked={allowTransfer}
                        onChange={setAllowTransfer}
                        label="Transfer"
                    />
                </div>
                {validationError && (
                    <small className={styles.errorText}>
                        {validationError}
                    </small>
                )}
                <small className={styles.helperText}>
                    Select at least one transaction type for this category
                </small>
            </div>

            <div className={styles.buttonGroup}>
                <button className={styles.addButton} onClick={handleAddCategory}>
                    Add Category
                </button>
                <button className={styles.cancelButton} onClick={() => {
                    setShowAddForm(false);
                    setNewCategoryName('');
                    setError('');
                }}>
                    Cancel
                </button>
            </div>
        </div>
    );
}

export default CategoryAddForm;
